// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().queryParams.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const getBaseQuery = async (args, api, extraOptions) => {
  const state = api.getState();
  const { baseUrl, user } = state.queryParams;

  // gracefully handle scenarios where data to generate the URL is missing
  if (!baseUrl) {
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        data: "No BASEURL found",
      },
    };
  }

  const urlEnd = typeof args === "string" ? args : args.url;
  // let adjustedUrl 
  // // construct a dynamically generated portion of the url
  const adjustedUrl = `${baseUrl}/workbench?user=${user}&${urlEnd}`;
  const adjustedArgs =
    typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };

  // provide the amended url and other params to the raw base query
  const result = await rawBaseQuery(adjustedArgs, api, extraOptions);
  
  // Handle network errors (offline scenarios)
  if (result.error) {
    const isNetworkError = result.error.status === 'FETCH_ERROR' || 
                          result.error.status === 'PARSING_ERROR' ||
                          !navigator.onLine;
    
    if (isNetworkError) {
      // Dispatch a custom action or trigger global notification
      // You can customize this message as needed
      const errorMessage = !navigator.onLine 
        ? "You appear to be offline. Please check your internet connection."
        : "Network error occurred. Please try again.";
      
      // Trigger a global toast notification
      // This will need to be handled by a global notification system
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: errorMessage,
          severity: 'error'
        }
      }));
    }
  }
  
  return result;
};

// Define a service using a base URL and expected endpoints
// TODO: create api list file to be ref'd and looped here
export const api = createApi({
  reducerPath: "api",
  baseQuery: getBaseQuery,
  tagTypes: ["Services"],
  endpoints: (builder) => ({
    getInboundTemplateList: builder.query({
      query: ({ account}) =>
        `action=get_inbound_templates_tree&account=${account}`,
    }),
    //Copy endpoint for use in New Template Wizard. Created to avoid addMatcher in treeSlice.js
    getCopyInboundTemplateList: builder.query({
      query: ({ account }) =>
        `action=get_inbound_templates_tree&account=${account}`,
    }),
    getInboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>(
        {
          url: `action=get_inbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&structonly=false`,
        }
      )
    }),
    //fetch inbound template data without triggering matcher
    getInboundTemplateData: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>
        `action=get_inbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&structonly=false`
    }),
    getInboundTemplateOriginal: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>
        `action=get_inbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&structonly=false&ignore_unsaved=true`,
    }),
    getPluginsList: builder.query({
      query: ({ account }) => {
        return `action=get_pluginList&account=${account}`
      }
    }),
    getPluginDetail: builder.query({
      query: ({ account, pluginName, data, workflow_group_name, template_name, location, source }) => ({
        url: `action=get_pluginDetails&account=${account}&pluginName=${pluginName}&workflow_group_name=${workflow_group_name}&template_name=${template_name}&location=${location}&source=${source || 'outbound'}`,
        method: 'POST',
        body: data
      })
    }),
    validateInboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName,user,data }) => ({
        url: `action=validate_inbound_template&account=${account}&template_name=${templateName}&workflow_group_name=${workflowGroupName}`,
        method: "POST",
        body: data,
      }),
    }),
    validateOutboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName,user,data }) => ({
        url: `action=validate_outbound_template&account=${account}&template_name=${templateName}&workflow_group_name=${workflowGroupName}`,
        method: "POST",
        body: data,
      }),
    }),
    validateWorkflowTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName,user,data }) => ({
        url: `action=validate_workflow_template&account=${account}&template_name=${templateName}&workflow_group_name=${workflowGroupName}`,
        method: "POST",
        body: data,
      }),
    }),
    getUnsavedTemplates :  builder.query({
      query: ({ account }) => ({
        url: `action=get_unsaved_templates&account=${account}`,
      }),
    }),
    getUnsavedInboundTemplates :  builder.query({
      query: ({ account }) => ({
        url: `action=get_unsaved_inbound_templates&account=${account}`,
      }),
    }),
    saveUserInboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName, data }) => ({
        url: `action=save_inbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${templateName}&store_unsaved_changes=TRUE`,
        method: "POST",
        body: data,
      }),
    }),
    saveInboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName, comment, data ,save_to_server }) => ({
        url: `action=save_inbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${templateName}&comment=${comment}&save_to_server=${save_to_server}`,
        method: "POST",
        body: data,
      }),
    }),
    saveAllTemplates: builder.query({
      query: ({ comment, data , save_to_server }) => ({
        url: `action=save_templates&comment=${comment}&save_to_server=${save_to_server}`,
        method: "POST",
        body: data,
      }),
    }),
    revertAllTemplates: builder.query({
      query: ({ data }) => ({
        url: `action=revert_templates`,
        method: "POST",
        body: data,
      }),
    }),
    getOutboundTemplateList: builder.query({
      query: ({ account}) =>
        `action=get_outbound_templates_tree&account=${account}`,
    }),
    saveUserOutboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName, data, source }) => ({
        url: `action=save_outbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${templateName}&store_unsaved_changes=TRUE&source=${source}`,
        method: "POST",
        body: data,
      }),
    }),
    saveOutboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName, comment, data, save_to_server }) => ({
        url: `action=save_outbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&comment=${encodeURIComponent(comment ?? "")}&save_to_server=${save_to_server}`,
        method: "POST",
        body: data,
      }),
    }),
    //Copy endpoint for use in New Template Wizard. Created to avoid addMatcher in treeSlice.js
    getCopyOutboundTemplateList: builder.query({
      query: ({ account }) =>
        `action=get_outbound_templates_tree&account=${account}`,
    }),
    getWorkflows: builder.query({
      query: ({ account}) =>
        `action=get_workflows&account=${account}`,
    }),
    getWorkflowsOriginal: builder.query({
      query: ({ account}) =>
        `action=get_workflows&account=${account}`,
    }),
    ///fetch workflows data without accidentally triggering matcher
    getWorkflowsCopy: builder.query({
      query: ({ account, user }) =>
        `action=get_workflows&account=${account}${user ? `&user=${encodeURIComponent(user)}` : ""}`,
    }),
    getWorkflow: builder.query({
      query: ({ account, workflow_group_name, workflow_name, workflowGroupName, templateName}) =>
        `action=get_workflow_template&account=${account}&workflow_group_name=${workflow_group_name || workflowGroupName}&workflow_name=${workflow_name || templateName}`,
    }),
    //fetch worfklow data for right side tree and trigger matcher in treeSlice.js
    getWorkflowTree: builder.query({
      query: ({ account, workflowGroupName, templateName}) =>
        `action=get_workflow_template&account=${account}&workflow_group_name=${workflowGroupName}&workflow_name=${templateName}`,
    }),
    //fetch workflow data without triggering matcher
    getWorkflowData: builder.query({
      query: ({ account,workflow_group_name,workflow_name}) =>
        `action=get_workflow_template&account=${account}&workflow_group_name=${workflow_group_name}&workflow_name=${workflow_name}`,
    }),
    getWorkflowOriginal: builder.query({
      query: ({ account,workflow_group_name,workflow_name}) =>
        `action=get_workflow_template&account=${account}&workflow_group_name=${workflow_group_name}&workflow_name=${workflow_name}&ignore_unsaved=true`,
    }),
    saveUserWorkflowTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName, data, source }) => ({
        url: `action=save_workflow_template&account=${account}&workflow_group_name=${workflowGroupName}&workflow_name=${templateName}&store_unsaved_changes=TRUE&source=${source}`,
        method: "POST",
        body: data,
      }),
    }),
    saveWorkflowTemplate: builder.query({
      query: ({ account, workflowGroupName, workflowName, templateName, data, save_to_server }) => {
        const wf = workflowName ?? templateName;
        return {
          url: `action=save_workflow_template&account=${account}&workflow_group_name=${workflowGroupName}&workflow_name=${encodeURIComponent(wf)}&save_to_server=${save_to_server}`,
          method: "POST",
          body: data,
        };
      },
    }),
    getOutboundTemplate: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>
        `action=get_outbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&structonly=false`,
    }),
    //fetch outbound template data without triggering matcher
    getOutboundTemplateData: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>
        `action=get_outbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&structonly=false`,
    }),
    getOutboundTemplateOriginal: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>
        `action=get_outbound_template&account=${account}&workflow_group_name=${workflowGroupName}&template_name=${encodeURIComponent(templateName)}&structonly=false&ignore_unsaved=true`,
    }),
    getEDIVersions: builder.query({
      query: ({ data_type, account }) =>
        `action=get_data_type_versions&data_type=${data_type}&account=${account}`,
    }),
    getNCPDPVersions: builder.query({
      query: ({ data_type, account }) =>
        `action=get_template_types&data_type=${data_type}&account=${account}`,
    }),
    getTransSets: builder.query({
      query: ({ data_type, account, data_type_version }) =>
        `action=get_trans_sets&data_type_version=${data_type_version}&data_type=${data_type}&account=${account}`,
    }),
    getTriggerEvents: builder.query({
      query: ({ account, data_type_version, message_type }) =>
        `action=get_trigger_event_combo_values&data_type_version=${data_type_version}&message_type=${message_type}&account=${account}`,
    }),
    getSchemaList: builder.query({
      query: ({ account, direction, pdc_name }) =>
        `action=get_schema_list&account_name=${account}&direction=${direction}&pdc_name=${pdc_name}`,
    }),
    updateWorkflowName: builder.query({
      query: ({ account, currentName, newName }) =>
        `action=rename_workflow_group&account=${account}&workflow_group_name=${encodeURIComponent(
          currentName
        )}&new_workflow_group_name=${encodeURIComponent(newName)}`,
    }),
    updateTemplateName: builder.query({
      query: ({ account, workflowGroupName, currentName, newName, is_userspace, is_server, is_inbound,is_overwrite }) =>
        `action=rename_inbound_template&account=${account}&workflow_group_name=${encodeURIComponent(
          workflowGroupName
        )}&template_name=${encodeURIComponent(
          currentName
        )}&template_rename=${encodeURIComponent(newName)}&is_userspace=${is_userspace}&is_server=${is_server}&is_inbound=${is_inbound}&is_overwrite=${is_overwrite}&update_dependencies=true`,
    }),
    checkInboundTemplateDependency: builder.query({
      query: ({ account, workflowGroupName, templateName }) =>
        `action=check_inbound_template_rename_dependencies&account=${account}&workflow_group_name=${encodeURIComponent(
          workflowGroupName
        )}&template_name=${encodeURIComponent(
          templateName
        )}`,
    }),
    checkWorkflowRenameDependency: builder.query({
      query: ({ account, workflowGroupName, workflowName, user }) =>
        `action=check_workflow_template_rename_dependencies&account=${account}&workflow_group_name=${encodeURIComponent(
          workflowGroupName
        )}&workflow_name=${encodeURIComponent(
          workflowName
        )}&user=${user}`,
    }),
    deleteWorkflowname: builder.query({
      query: (arg) => {
        const { account, workflowName, is_userspace, is_server } = arg;
        return {
          url: `action=delete_workflow_group&account=${account}&workflow_group_name=${encodeURIComponent(
            workflowName
          )}&is_userspace=${is_userspace}&is_server=${is_server}`,
        };
      },
    }),
    addWorkflowGroup: builder.mutation({
      query: (arg) => ({
        url: `action=new_workflow_group&account=${arg.account}&workflow_group_name=${arg.workflowGroupName}&structonly=false`,
        method: "POST",
        body: arg,
      }),
    }),
    addWorkflowTemplate: builder.query({
      query: ({ account, workflow_group_name,workflow_name,type,source_workflow_name,source_workflow_group_name,source_account }) => {
        return `action=create_workflow_template&account=${account}&workflow_group_name=${workflow_group_name}&workflow_name=${workflow_name}&type=${type}&source_workflow_name=${source_workflow_name}&source_workflow_group_name=${source_workflow_group_name}&source_account=${source_account}`
      }
    }),
    addInboundTemplate: builder.mutation({
      query: (arg) => {
        const { account, workflowGroup, templateName } = arg;
        return {
          url: `action=new_inbound_template&account=${account}&workflow_group_name=${workflowGroup}&template_name=${templateName}&structonly=false`,
        };
      },
    }),
    deleteInboundTemplate: builder.mutation({
      query: (arg) => {
        const { account, workflowName, templateName, is_userspace, is_server,is_inbound, ext } = arg;
        return {
          url: `action=delete_inbound_template&account=${account}&workflow_group_name=${encodeURIComponent(
            workflowName
          )}&template_name=${encodeURIComponent(templateName)}&is_userspace=${is_userspace}&is_server=${is_server}&is_inbound=${is_inbound}&ext=${ext}`,
        };
      },
    }),
    deleteOutboundTemplate: builder.mutation({
      query: (arg) => {
        const { account, workflowName, templateName, is_userspace, is_server,is_inbound, ext } = arg;
        return {
          url: `action=delete_outbound_template&account=${account}&workflow_group_name=${encodeURIComponent(
            workflowName
          )}&template_name=${encodeURIComponent(templateName)}&is_userspace=${is_userspace}&is_server=${is_server}&is_inbound=${is_inbound}&ext=${ext}`,
        };
      },
    }),
    copyInboundTemplate: builder.mutation({
      query: (arg) => {
        const {
          account,
          sourceWorkflowName,
          sourceAccount,
          sourceTemplateName,
          destinationWorkflowName,
          newTemplateName,
          is_overwrite,
          is_userspace,
          is_server,
          is_inbound
        } = arg;
        return {
          url: `action=copy_inbound_template&account=${account}&source_account=${sourceAccount}&source_workflow_group_name=${encodeURIComponent(
            sourceWorkflowName
          )}&source_template_name=${encodeURIComponent(
            sourceTemplateName
          )}&workflow_group_name=${encodeURIComponent(
            destinationWorkflowName
          )}&template_name=${encodeURIComponent(
            newTemplateName
          )}&is_overwrite=${is_overwrite}&is_userspace=${is_userspace}&is_server=${is_server}&is_inbound=${is_inbound}`,
        };
      },
    }),
    copyOutboundTemplate: builder.mutation({
      query: (arg) => {
        const {
          account,
          sourceWorkflowName,
          sourceAccount,
          sourceTemplateName,
          destinationWorkflowName,
          newTemplateName,
          is_overwrite,
          is_userspace,
          is_server,
          is_inbound
        } = arg;
        return {
          url: `action=copy_outbound_template&account=${account}&source_account=${sourceAccount}&source_workflow_group_name=${encodeURIComponent(
            sourceWorkflowName
          )}&source_template_name=${encodeURIComponent(
            sourceTemplateName
          )}&workflow_group_name=${encodeURIComponent(
            destinationWorkflowName
          )}&template_name=${encodeURIComponent(
            newTemplateName
          )}&is_overwrite=${is_overwrite}&is_userspace=${is_userspace}&is_server=${is_server}&is_inbound=${is_inbound}`,
        };
      },
    }),
    moveInboundTemplate: builder.mutation({
      query: (arg) => {
        const {
          account,
          templateName,
          sourceWfg,
          destiationWfg,
          is_overwrite,
          is_userspace,
          is_server
        } = arg;
        return {
          url: `action=move_inbound_template&account=${account}&template_name=${templateName}&source_workflow_group_name=${sourceWfg}&destination_workflow_group_name=${destiationWfg}&is_overwrite=${is_overwrite}&is_userspace=${is_userspace}&is_server=${is_server}`,
        };
      },
    }),
    moveOutboundTemplate: builder.mutation({
      query: (arg) => {
        const {
          account,
          templateName,
          sourceWfg,
          destiationWfg,
          is_overwrite,
          is_userspace,
          is_server
        } = arg;
        return {
          url: `action=move_outbound_template&account=${account}&template_name=${templateName}&source_workflow_group_name=${sourceWfg}&destination_workflow_group_name=${destiationWfg}&is_overwrite=${is_overwrite}&is_userspace=${is_userspace}&is_server=${is_server}`,
        };
      },
    }),
    get_latest_user_revision_history: builder.mutation({
      query: (arg) => {
        const { account, user, path } = arg;
        return {
          url: `action=get_latest_user_revision_history&account=${account}&path=${path}`,
        };
      },
    }),
    get_user_revision_history: builder.mutation({
      query: (arg) => {
        const { account, user, path } = arg;
        return {
          url: `action=get_user_revision_history&account=${account}&path=${path}&pretty=true`,
        };
      },
    }),
    get_user_template_by_revision_history_id: builder.mutation({
      query: (arg) => {
        const { account, user, path, revid, user_revid } = arg;
        return {
          url: `action=get_user_template_by_revision_history_id&account=${account}&path=${path}&revid=${revid}&user_revid=${user_revid}&pretty=true`,
        };
      },
    }),
    getDataDepotList: builder.query({
      query: ({account}) => `action=get_data_depot_list&account=${account}`,
    }),
    getDataDepotFieldList:builder.query({
      query:({account, dataDepotName}) => `action=get_data_depot_field_list&data_depot_name=${dataDepotName}&account=${account}`
    }),
    getAccounts:builder.query({
      query:(arg)=> `action=get_accounts`
    }),
    validateDTDFile:builder.mutation({
      query:(arg) =>({
        url:`action=validate_dtd_file&account=${arg.account}`,
        method:'POST',
        body:arg.data,
        formData: true,
      }),
    }),
    addDTDInbondTemplate:builder.mutation({
      query:({account,rootElement,newTemplateName,workflowGroup,fileName}) =>({
        url:`action=create_dtd_inbound_template&account=${account}&root=${rootElement}&filename=${fileName}&workflow_group_name=${workflowGroup}&template_name=${newTemplateName}`
      })
    }),
    testPortioningLogic: builder.mutation({
      query:(arg) => ({
        url: `action=record_data_portioning&account=${arg.account}`,
        method:'POST',
        body:arg.data,
      })
    }),
    getHelpTreeList: builder.query({
      query: (args) => `action=get_documentation_list&language=en&search_str=&account=${args.account}`
    }), 
    getSearchHelpDocs: builder.query({
      query: (args) =>
        `action=search_documentation&keyword=${args.keyword}&language=en`,
    }),
    searchHelpDoc: builder.query({
      query: (args) => `action=get_searched_document_formatted&keyword=${args.keyword}&source_doc=${args.searchString}`
    }),
    validateDatePattern:builder.query({
      query:({datePattern, select}) => `action=validate_date_pattern&date_pattern=${datePattern}`
    }),
    number_formatter:builder.query({
      query:(args) => {
        const {account,user,data_string,pattern,precision_value,check_box} = args;
        return { url:`action=number_formatter&account=${account}&data_string=${data_string}&pattern=${encodeURIComponent(pattern)}&precision_value=${precision_value}&check_box=${check_box}`}
      }
    }),
    connect_to_selected_db:builder.query({
      query:(args) => {
        const {account,direction,pdc_name} = args;
        return { url:`action=connect_to_selected_db&account=${account}&direction=${direction}&pdc_name=${pdc_name}`}
      }
    }),
    //run without addMatcher
     getDatabaseTableInfo:builder.query({
      query:(args) => {
        const {account, direction, pdc_name, schema_name} = args;
        return { url:`action=get_database_table_info&account=${account}&direction=${direction}&pdc_name=${pdc_name}&schema_name=${schema_name}`}
      }
    }),
    get_database_table_info:builder.query({
      query:(args) => {
        const {account, user, direction, pdc_name, schema_name} = args;
        return { url:`action=get_database_table_info&account=${account}&direction=${direction}&pdc_name=${pdc_name}&schema_name=${schema_name}`}
      }
    }),
    get_database_column_info:builder.query({
      query:(args) => {
        const {account,user,table_name, direction, pdc_name, schema_name} = args;
        return { url:`action=get_database_column_info&account=${account}&table_name=${table_name}&direction=${direction}&pdc_name=${pdc_name}&schema_name=${schema_name}`}
      }
    }),
    get_metadata:builder.query({
      query:(args) => {
        const {account, direction, table_name, pdc_name, sort, schema} = args;
        return { url:`action=get_metadata&account_name=${account}&direction=${direction}&pdc_name=${pdc_name}&table_name=${table_name}&sort=${sort}&schema=${schema}`}
      }
    }),
    get_top_25_records:builder.query({
      query:(args) => {
        const {account, direction, table_name, pdc_name, encoding, schema} = args;
        return { url:`action=get_top_25_records&account_name=${account}&direction=${direction}&pdc_name=${pdc_name}&table_name=${table_name}&encoding=${encoding}&schema=${schema}`}
      }
    }),
    get_sql_result:builder.query({
      query:(args) => {
        const {account, direction, pdc_name, encoding, schema, body} = args;
        return { 
          url:`action=get_sql_result&account_name=${account}&direction=${direction}&pdc_name=${pdc_name}&encoding=${encoding}&schema=${schema}`,
          body : body,
          method: "POST"
        }
      }
    }),
    compare_sql:builder.query({
      query:(args) => {
        return { 
          url:`action=compare_sql`,
          body : args.body,
          method: "POST"
        }
      }
    }),
    get_session_var_names:builder.query({
      query:(args) => {
        const {account,user,workflow_group_name,workflow_name,current_item_id} = args;
        return { url:`action=get_session_var_names&account=${account}&workflow_group_name=${workflow_group_name}&workflow_name=${workflow_name}${current_item_id ? `&current_item_id=${current_item_id}` : ""}`}
      }
    }),
    // api to get values for hl7 data type dropdown
    getDatatypeProperties:builder.query({
      query:(args) =>{
        const {account,version,report_type} = args;
        return {
          url:`action=get_datatype_properties&account=${account}&version=${version}&report_type=${report_type}`
        }
      }
    }),
    // api to get hl7 segment Id
    getSegmentIds:builder.query({
      query:(args) =>{
        const {account,version,report_type} = args;
        return {
          url:`action=get_segment_ids&account=${account}&version=${version}&report_type=${report_type}`
        }
      }
    }),
    modify_account_data:builder.query({
      query:(args) =>{
        const {account, user} = args;
        return {
          url:`action=modify_account_data&account=${account}`,
          method: 'POST',
          body: args.data
        }
      }
    }),
    // api to get repository directories in new template wizard
    getRepositoryTemplates:builder.query({
      query:(args) =>{
        const {account,template_type} = args;
        return{
          url:`action=get_repo_dir&account=${account}&template_type=${template_type}`
        }
      }
    }),
    // endpoint to get repository templates based on selected repo directory in new template wizard
    getRepositoryFiles: builder.query({
      query: (args) => {
        const { account, template_type, path, repo_type, release_key, title } =
          args;
        return {
          url: `action=get_repo_files&account=${account}&template_type=${template_type}&path=${path}&repo_type=${repo_type}&release_key=${release_key}&title=${title}`,
        };
      },
    }),
    getAccountData:builder.query({
      query:(args) =>{
        const {account,user} = args;
        return{
          url:`action=get_account_data&account=${account}`
        }
      }
    }),
    addDataDepot: builder.query({
      query:(args) =>{
        const {account, depotName} = args;
        return{
          url:`action=add_data_depot&account=${account}&data_depot_name=${depotName}`
        }
      }
    }),
    copyDataDepot: builder.query({
      query:(args) =>{
        const {account, depotName, newDepotName} = args;
        return{
          url:`action=copy_data_depot&account=${account}&data_depot_name=${depotName}&copy_data_depot_name=${newDepotName}`
        }
      }
    }),
    addDataDepotField: builder.query({
      query:(args) =>{
        const {account, depotName, fieldName, type} = args;
        return{
          url:`action=add_data_depot_field&account=${account}&data_depot_name=${depotName}&data_depot_field_name=${fieldName}&data_depot_field_type=${type}`
        }
      }
    }),
    updateDataDepotField: builder.query({
      query:(args) =>{
        const {account, depotName, fieldName, fieldType, newName, newType} = args;
        return{
          url:`action=update_data_depot_field&account=${account}&data_depot_name=${depotName}&data_depot_field_name=${fieldName}&data_depot_field_type=${fieldType}&data_depot_new_field_name=${newName}&data_depot_new_field_type=${newType}`
        }
      }
    }),
    deleteDataDepot: builder.query({
      query:(args) =>{
        const {account, depotName} = args;
        return{
          url:`action=delete_data_depot&account=${account}&data_depot_name=${depotName}`
        }
      }
    }),
    deleteDataDepotField: builder.query({
      query:(args) =>{
        const {account, depotName, fieldName} = args;
        return{
          url:`action=delete_data_depot_field&account=${account}&data_depot_name=${depotName}&data_depot_field_name=${fieldName}`
        }
      }
    }),
    //endpoint to create new inbound template
    addNewInboundTemplate: builder.query({
      query: (args) => {
        const {
          account,
          newTemplateName,
          workflowGroup,
          file_type,
          copy_file,
          is_inbound,
          release_key,
          sef_edi_set,
          is_repo,
          title,
          generate_ack
        } = args;
        return {
          url: `action=new_inbound_template&account=${account}&template_name=${newTemplateName}&workflow_group_name=${workflowGroup}&file_type=${file_type}&copy_file=${copy_file}&is_inbound=${is_inbound}&release_key=${release_key}&sef_edi_set=${sef_edi_set}&title=${title}&is_repo=${is_repo}&generate_ack=${generate_ack}`,
        };
      },
    }),
     // to create new edi 997 template
    addNewEDI997Template: builder.query({
      query: (args) => {
        const { account, newTemplateName, workflowGroup, file_type, is_inbound, release_key, sef_edi_set, is_repo, generate_997, path } = args;
        return {
          url: `action=new_inbound_template&account=${account}&template_name=${newTemplateName}&workflow_group_name=${workflowGroup}&file_type=${file_type}&copy_file=${path}&is_inbound=${is_inbound}&release_key=${release_key}&sef_edi_set=${sef_edi_set}&is_repo=${is_repo}&generate_997=${generate_997}`,
        }
      }
    }),
    addNewInboundTemplateWithFormData: builder.mutation({
      query: (arg) => ({
        url: `action=new_inbound_template&account=${arg.requestData.account}&template_name=${arg.requestData.newTemplateName}&workflow_group_name=${arg.requestData.workflowGroup}&file_type=${arg.requestData.file_type}&is_inbound=${arg.requestData.is_inbound}&is_schema=${arg.requestData.is_schema}&field_delim=${arg.requestData.field_delim}&record_delim=${arg.requestData.record_delim}&title=${arg.requestData.title}&sef_edi_set=${arg.requestData.sef_edi_set}&release_key=${arg.requestData.release_key}`,
        method: "POST",
        body: arg.data,
        formData: true,
        contentType: false,
        processData: false,
      }),
    }),
    getXSDRoots: builder.mutation({
      query: (arg) => ({
        url: `action=get_xsd_roots&account=${arg.account}&template_type=${arg.template_type}`,
        method: "POST",
        body: arg.data,
        formData: true,
      }),
    }),
    getEDISet: builder.mutation({
      query: (arg) => ({
        url: `action=load_sef_set&account=${arg.account}`,
        method: "POST",
        body: arg.data,
        formData: true,
      }),
    }),
    addDataFromFile: builder.mutation({
      query:(arg) =>({
        url:`action=quick_add_field&field_delimiter=${arg.field_delimiter}&record_delimiter=${arg.record_delimiter}`,
        method:'POST',
        body:arg.data,
        formData:true,
        contentType: false,
        processData: false,
      })
    }),
    getTradingPatners: builder.query({
      query: ({ account }) => {
        return `action=get_trading_partners&account=${account}`;
      },
    }),
    createNewTradingPartner: builder.query({
      query: ({ account, tp_name }) => {
        return `action=create_trading_partner&account=${account}&tp_name=${tp_name}`;
      },
    }),
    renameExistingTradingPartner: builder.query({
      query: ({ account, new_tp_name, old_tp_name }) => {
        return `action=rename_trading_partner&account=${account}&new_tp_name=${new_tp_name}&old_tp_name=${old_tp_name}`;
      },
    }),
    deleteTradingPartner: builder.query({
      query: ({ account, tp_name }) => {
        return `action=delete_trading_partner&account=${account}&tp_name=${tp_name}`;
      },
    }),
    copyTradingPartner: builder.query({
      query: ({account, old_tp_name, new_tp_name}) => {
        return`action=copy_trading_partner&account=${account}&old_tp_name=${old_tp_name}&new_tp_name=${new_tp_name}`
      }
    }),
    getTradingPartnerData: builder.query({
      query: ({account,tp_name }) => {
        return `action=get_trading_partner_key_values&account=${account}&tp_name=${tp_name}`
      }
    }),
    updateTradingPartnerDetails: builder.mutation({
      query: ({account, data}) => ({
       url: `action=update_value_for_specific_trading_partner&account=${account}`,
       method: 'POST',
       body: data,
       contentType: 'application/json'
      })
    }),
    getTradingPartnersKeys: builder.query({
      query: ({ account }) => {
        return `action=get_trading_partner_keys&account=${account}`
      }
    }),
    getTradingPartnerKeysDetails: builder.query({
       query:({account, tp_name}) => {
        return `action=get_trading_partner_details&account=${account}&tp_name=${tp_name}`
       }
    }),
    saveTradingPartnerKeyDetails: builder.mutation({
     query: ({account,data}) => ({
      url: `action=save_trading_partner_key_details&account=${account}`,
      method: 'POST',
      body: data,
      contentType: 'application/json'
     })
    }),
    deleteTradingPartnerKey: builder.query({
      query: ({account, tp_key_name}) => {
        return `action=delete_trading_partner_keys&account=${account}&tp_key_name=${tp_key_name}`
      }
    }),
    renameTradingPartnerKey: builder.query({
      query: ({account, new_tpkey, old_tpkey}) => {
        return `action=rename_trading_partner_key&account=${account}&new_tpkey=${encodeURIComponent(new_tpkey)}&old_tpkey=${encodeURIComponent(old_tpkey)}`
      }
    }),
    get_edi_codelist: builder.query({
      query: ({ account, user, data_element_id, release_uid, transaction_set_id, is_parent_field_group,field_desc, parent_segment_id, parent_name, parent_sequence, parent_position_number }) => {
        return `action=get_edi_codelist&account=${account}&data_element_id=${data_element_id}&release_uid=${release_uid}&transaction_set_id=${transaction_set_id}&is_parent_field_group=${is_parent_field_group}&field_desc=${encodeURIComponent(field_desc)}&parent_segment_id=${parent_segment_id}&parent_name=${encodeURIComponent(parent_name)}&parent_sequence=${parent_sequence}&parent_position_number=${parent_position_number}`
      }
    }),
    getPdcList: builder.query({
      query: ({ account, direction, datatype }) =>
        `action=get_predefined_connections_list&account=${account}&direction=${direction}&datatype=${datatype}`,
    }),
    getAllPdcDetail: builder.query({
      query: ({ account, direction }) =>
        `action=get_predefined_connections_list&account=${account}&direction=${direction}&get_details=true`
    }),
    getPdcDetail: builder.query({
      query: ({ account, direction, name }) =>
        `action=get_predefined_connection&account=${account}&direction=${direction}&name=${name}`
    }),
    createPdc: builder.mutation({
      query: ({ account, direction, name, body, oldPdcName }) => ({
        url: `action=save_predefined_connection&account=${account}&direction=${direction}&name=${name}&old_pdc_name=${oldPdcName}`,
        method: 'POST',
        body: body,
        contentType: 'application/json'
      }),
    }),
    testPdcConnection: builder.mutation({
      query: ({ account, direction, body }) => ({
        url: `action=test_predefined_connection&account=${account}&direction=${direction}`,
        method: 'POST',
        body: body,
        contentType: 'application/json'
      }),
    }),
    deletePdc: builder.query({
      query: ({ account, direction, name }) =>
        `action=delete_predefined_connection&account=${account}&direction=${direction}&name=${name}`
    }),
    getEcryptionList: builder.query({
      query: () => {
        return `action=get_encryption_list_pdc`
      }
    }),
    getServerCommonNameList: builder.query({
      query: ({ user, account } = {}) => {
        const u = user ? `&user=${encodeURIComponent(user)}` : "";
        const a = account ? `&account=${encodeURIComponent(account)}` : "";
        return `action=get_server_cluster_common_name_list${u}${a}`;
      }
    }),
    getSchedulerHomeData: builder.query({
      query: ({ user, account, workflow_group, assigned_workflow, status, schedule_name }) => {
        return `action=get_scheduler_home_data&user=${encodeURIComponent(user || "")}&account=${account}&workflow_group=${workflow_group || "ALL"}&assigned_workflow=${encodeURIComponent(assigned_workflow || "")}&status=${status || "ALL"}&schedule_name=${encodeURIComponent(schedule_name || "")}`;
      },
    }),
    getSchedulerReferenceData: builder.query({
      query: ({ user, account }) => {
        return `action=get_scheduler_reference_data&user=${encodeURIComponent(user || "")}&account=${account}`;
      },
    }),
    getScheduleExecHistory: builder.query({
      query: ({ user, account, workflow_group, schedule_name, row_count = 50 }) => {
        return `action=get_schedule_exec_history&user=${encodeURIComponent(user || "")}&account=${account}&workflow_group=${encodeURIComponent(workflow_group)}&schedule_name=${encodeURIComponent(schedule_name)}&row_count=${row_count}`;
      },
    }),
    getScheduleDetails: builder.query({
      query: ({ user, account, workflow_group_name, schedule_name }) => {
        return `action=get_schedule&user=${encodeURIComponent(user || "")}&account=${account}&workflow_group_name=${encodeURIComponent(workflow_group_name)}&schedule_name=${encodeURIComponent(schedule_name)}&structonly=false`;
      },
    }),
    saveScheduleDetails: builder.mutation({
      query: ({ user, account, workflow_group_name, schedule_name, data, store_unsaved_changes = false, save_to_server = false }) => ({
        url:
          `action=save_schedule&user=${encodeURIComponent(user || "")}&account=${account}&workflow_group_name=${encodeURIComponent(workflow_group_name)}&schedule_name=${encodeURIComponent(schedule_name)}` +
          (store_unsaved_changes ? `&store_unsaved_changes=TRUE` : "") +
          (save_to_server ? `&save_to_server=TRUE` : ""),
        method: "POST",
        body: data,
        responseHandler: async (response) => {
          const text = await response.text();
          if (!text || !text.trim()) {
            return { current_state: "SUCCESS", message: "Saved (empty response body)." };
          }
          try {
            return JSON.parse(text);
          } catch (e) {
            return { current_state: "ERROR", message: text };
          }
        },
      }),
    }),
    saveTemplates: builder.mutation({
      query: ({ comment = "", save_to_server = true, data }) => ({
        url: `action=save_templates&comment=${encodeURIComponent(comment)}&save_to_server=${save_to_server ? "TRUE" : "FALSE"}`,
        method: "POST",
        body: data,
        responseHandler: async (response) => {
          const text = await response.text();
          if (!text || !text.trim()) {
            return { current_state: "SUCCESS", message: "Saved templates (empty response body)." };
          }
          try {
            return JSON.parse(text);
          } catch (e) {
            return { current_state: "ERROR", message: text };
          }
        },
      }),
    }),
    changeScheduleStatus: builder.mutation({
      query: ({ user, account, schedule_name, schedule_key, mode, request_ip }) => ({
        url: `action=change_schedule_status&user=${encodeURIComponent(user || "")}&account=${account}&schedule_name=${encodeURIComponent(schedule_name)}&schedule_key=${encodeURIComponent(schedule_key)}&mode=${mode}&request_ip=${encodeURIComponent(request_ip || "")}`,
      }),
    }),
    deleteSchedule: builder.mutation({
      query: ({ user, account, workflow_group_name, schedule_name, is_userspace = true, is_server = true, request_ip = "", ext = "json" }) => ({
        url: `action=delete_schedule&user=${encodeURIComponent(user || "")}&account=${account}&workflow_group_name=${encodeURIComponent(workflow_group_name)}&schedule_name=${encodeURIComponent(schedule_name)}&is_userspace=${is_userspace}&is_server=${is_server}&request_ip=${encodeURIComponent(request_ip)}&ext=${ext}`,
      }),
    }),
    getCompressionList: builder.query({
      query: () => {
        return `action=get_compression_list`
      }
    }),
    getPrivateKeys: builder.query({
      query: ({private_key})  => {
        return `action=get_ssl_private_key_aliases&private_key=${private_key}`
      }
    }),
    getWsSecurityKeyAliases: builder.query({
      query: ({keystore, keystorePassword, keystoreType, account, workflowGroup, user, privateKey=false, pretty=true, structOnly=false}) => {
        return `action=get_ws_security_key_aliases&keystore_path=${keystore}&keystore_password=${keystorePassword}&keystore_type=${keystoreType}&account=${account}&workflow_group=${workflowGroup}&user=${user}&private_key=${privateKey}&pretty=${pretty}&structOnly=${structOnly}`
      }
    }),
    getAS2Ids: builder.query({
      query: () => {
        return `action=get_as2_ids`
      }
    }),
    getAdapterList: builder.query({
      query: ({ direction }) => {
        return `action=get_adapter_list&direction=${direction}`
      }
    }),
    getDatabaseDriverList: builder.query({
      query: () => {
        return `action=get_database_driver_list`
      }
    }),
    getSslVersion: builder.query({
      query: ({account, adapterType}) => {
        return `action=get_ssl_versions_list&account=${account}${adapterType !== '' && `&adapter_type=${adapterType}`}`;
      }
    }),
    processQuickAddCustomizeScreen: builder.mutation({
      query:({template_type,hl7,data}) => ({
        url:`action=quick_add_customize_screen&template_type=${template_type}&hl7=${hl7}`,
        method:'POST',
        body:data,
      })
    }),
    postQuickAddColumnOrder: builder.mutation({
      query:({data})=> ({
        url:`action=quick_add_column_order`,
        method:'POST',
        body:data
      })
    }),
    getQuickAddColumnOrder: builder.query({
      query:() =>{
        return `action=quick_add_column_order`
      }
    }),
    getServerRevisionhistory: builder.query({
      query:({account,path}) => {
        return `action=get_server_revision_history&account=${account}&path=${path}`
      }
    }),
    getTemplateByServerRevId:builder.query({
      query:({account,path,revId}) => {
       return `action=get_server_template_by_revid&account=${account}&path=${path}&revid=${revId}`
      }
    }),
    renameOutboundTemplate: builder.query({
      query: ({
        account,
        workflowGroupName,
        currentName,
        newName,
        is_userspace,
        is_server,
        is_inbound,
        is_overwrite
      }) => {
        return `action=rename_outbound_template&account=${account}&workflow_group_name=${encodeURIComponent(
          workflowGroupName
        )}&template_name=${encodeURIComponent(
          currentName
        )}&template_rename=${encodeURIComponent(
          newName
        )}&is_userspace=${is_userspace}&is_server=${is_server}&is_inbound=${is_inbound}&is_overwrite=${is_overwrite}`;
      },
    }),
    testRegularExpression: builder.mutation({
      query: ({ data }) => ({
        url: `action=global_substitution`,
        method: 'POST',
        body: data,
      }),
    }),
    testGlobalSubstitutionJson: builder.mutation({
      query: ({ data }) => ({
        url: `action=global_substitution_json`,
        method: 'POST',
        body: data,
      }),
    }),
    getConversionList: builder.query({
      query: ({ account }) => {
        return `action=get_conversion_type_list&account=${account}&param_functions=false`
      }
    }),
    testFieldPortioningData: builder.query({
      query: ({ account, data }) => ({
        url: `action=advanced_data_portioning&account=${account}`,
        method: 'POST',
        body: data
      })
    }),
    testReplacementData: builder.query({
      query: ({ account, data }) => ({
        url: `action=advanced_data_portioning&account=${account}`,
        method: 'POST',
        body: data
      })
    }),
    testStrippingData: builder.query({
      query: ({ account, data }) => ({
        url: `action=stripping&account=${account}`,
        method: 'POST',
        body: data
      })
    }),
    testAlphaMaskData: builder.query({
      query: ({ account, data, mask }) => {
        return `action=advanced_alpha_masking&account=${account}&test_data=${data}&mask=${mask}`
      }
    }),
    testConversionData: builder.query({
      query: ({ account, conversion_type, data }) => {
        return `action=advanced_conversion&account=${account}&conversion_type=${conversion_type}&test_data=${encodeURIComponent(data)}`
      }
    }),
    getBridgegateConfig: builder.query({
      query: ({ account }) => {
        return `action=get_bridgegate_config&account=${account}`
      }
    }),
    sendLastSelectedTab: builder.query({
      query:({tab, account})=> ({
        url: `action=save_workbench_last_tab&last_tab=${tab}&account=${account}`,
        method:'POST',
        body: {account: account, last_tab: tab}
      })
    }),
    renameWorkflowTemplate: builder.query({
      query:({account,workflowGroupName,currentName,newName,is_userspace,is_server,is_overwrite}) => {
        return `action=rename_workflow_template&account=${account}&workflow_group_name=${workflowGroupName}&workflow_name=${currentName}&workflow_rename=${newName}&is_server=${is_server}&is_userspace=${is_userspace}&is_overwrite=${is_overwrite}&update_dependencies=true`
      }
    }),
    deleteWorkflowTemplate: builder.query({
      query:({account,workflowName,templateName,is_userspace,is_server, ext}) => {
        return `action=delete_workflow_template&account=${account}&workflow_group_name=${workflowName}&workflow_name=${templateName}&is_userspace=${is_userspace}&is_server=${is_server}&ext=${ext}`
      }
    }),
    copyWorkflowTemplate: builder.query({
      query: ({account,sourceWorkflowName,sourceTemplateName,destinationWorkflowName,newTemplateName,is_overwrite,is_userspace,is_server }) => {
        return `action=copy_workflow_template&account=${account}&source_workflow_group_name=${sourceWorkflowName}&destination_workflow_group_name=${destinationWorkflowName}&source_workflow_name=${sourceTemplateName}&destination_workflow_name=${newTemplateName}&is_server=${is_server}&is_userspace=${is_userspace}&is_overwrite=${is_overwrite}`
      }
    }),
    moveWorkflowTemplate: builder.query({
      query: ({account,is_server,is_userspace,sourceWfg,destiationWfg,templateName,is_overwrite}) => {
        return `action=move_workflow_template&account=${account}&is_server=${is_server}&is_userspace=${is_userspace}&source_workflow_group_name=${sourceWfg}&destination_workflow_group_name=${destiationWfg}&source_workflow_name=${templateName}&destination_workflow_name=${templateName}&is_overwrite=${is_overwrite}`
      }
    }),
    deployInboundTemplateToServer: builder.mutation({
      query: ({account, workflowGroupName, templateName, comment}) => {
        return {
          url: `action=deploy_inbound_template_to_server&account=${account}&workflow_group_name=${encodeURIComponent(workflowGroupName)}&template_name=${encodeURIComponent(templateName)}&comment=${encodeURIComponent(comment || 'Deploy to Server')}`,
          method: "POST"
        }
      }
    }),
    deployOutboundTemplateToServer: builder.mutation({
      query: ({account, workflowGroupName, templateName, comment}) => {
        return {
          url: `action=deploy_outbound_template_to_server&account=${account}&workflow_group_name=${encodeURIComponent(workflowGroupName)}&template_name=${encodeURIComponent(templateName)}&comment=${encodeURIComponent(comment || 'Deploy to Server')}`,
          method: "POST"
        }
      }
    }),
    deployWorkflowTemplateToServer: builder.mutation({
      query: ({account, workflowGroupName, templateName, comment, source}) => {
        let url = `action=deploy_workflow_template_to_server&account=${account}&workflow_group_name=${encodeURIComponent(workflowGroupName)}&workflow_name=${encodeURIComponent(templateName)}&comment=${encodeURIComponent(comment || 'Deploy to Server')}`;
        if (source) url += `&source=${encodeURIComponent(source)}`;
        return {
          url,
          method: "POST"
        }
      }
    }),
    getTrackingReports : builder.query ({
      query: ({user, account})  =>({
        url: `action=get_tracking_reports&user=${user}&account=${account}`
      })
    }),
    getTrackingReportFields : builder.query ({
      query: ({user, account, reportName, statementType})  =>({
        url: `action=get_tracking_report_fields&user=${user}&account=${account}&reportName=${reportName}${statementType ? `&statementType=${statementType}` : ""}`
      })
    }),
    createTrackingReport : builder.query ({
      query: ({data})  =>({
        url: `action=create_tracking_report&account=${data.account_name}&user=${data.user_name}`,
        method: "POST",
        body: data,
      })
    }),
    deleteTrackingReport : builder.query ({
      query: ({data})  =>({
        url: `action=delete_tracking_report&account=${data.account_name}&user=${data.user_name}`,
        method: "POST",
        body: data,
      })
    }),
    updateTrackingReport : builder.query ({
      query: ({data})  =>({
        url: `action=update_tracking_report&account=${data.account_name}&user=${data.user_name}`,
        method: "POST",
        body: data,
      })
    }),
    getAiAutoMapping: builder.query({
      query: ({user, account, WfGroupName_in, inbound, WfGroupName_out, outbound, map_only_selected}) => {
        let url = `action=get_ai_automapping&user=${user}&account=${account}&WfGroupName_in=${WfGroupName_in}&inbound=${inbound}&WfGroupName_out=${WfGroupName_out}&outbound=${outbound}`;
        if (map_only_selected) {
          url += `&map_only_selected=${map_only_selected}`;
        }
        return { url };
      }
    }),
    trackingReportAutomapping : builder.query ({
      query: ({data})  =>({
        url: `action=tracking_report_automapping&account=${data.account_name}&user=${data.user_name}&statement_type=${data.statement_type}&selected_tr_name=${data.selected_tr_name}&in_template=${data.in_template}&out_template=${data.out_template}&template_name=${data.template_name}&wfg=${data.wfg}&is_workflow=${data.is_workflow}`,
        method: "GET",
      })
    }),
    getTemplateFile: builder.query({
      query: ({user, account, relativePath}) => ({
        url: `action=get_template_file&user=${user}&account=${account}&relative_path=${relativePath}`,
        transformResponse: (response) => {
          return response;
        },
        responseHandler: (response) => response.text(),
      })
    }),
    getGetDataAdapterList: builder.query({
      query: ({account}) => ({
        url:`action=get_data_driver_list&account=${account}`
      })
    }),
    getSendDataAdapterList: builder.query({
      query: ({account}) => ({
        url:`action=send_data_driver_list&account=${account}`
      })
    }),
    getSessionData: builder.query({
      query: ({account, user, workflow_group_name, workflow_name, source_record_name, wf_item, outbound_condition_name, variable_name}) => ({
        url:`action=get_session_data&account=${account}&user=${user}&workflow_group_name=${workflow_group_name}&workflow_name=${workflow_name}&source_record_name=${source_record_name}&wf_item=${wf_item}&outbound_condition_name=${outbound_condition_name}&variable_name=${variable_name}`
      })
    }),
    getWsdlCombos: builder.query({
      query: ({user, account, fileUrl, wsdl_cache, old_path, refresh}) => ({
        url:`action=get_wsdl_combos&user=${user}&account=${account}&auth=NONE&wsdl_fileorurl=${fileUrl}&wsdl_cache=${wsdl_cache}&old_path=${old_path}&refresh=${refresh}`,
      })
    }),
    workflowDiff: builder.query({
      query: ({ account, user, workflow_group_name, workflow_name, template_name, is_server }) => ({
        url: `workflow_group_name=${workflow_group_name}&action=get_workflow_diff&account=${account}&workflow_name=${workflow_name}&html_only=true`
      }),
      transformResponse: (response) => {
        // Process the plain text response here
        return response; // Return the processed response
      },
      responseHandler: (response) => response.text(),
    }),
    outboundDiff: builder.query({
      query: ({ account, user, workflow_group_name, workflow_name, template_name, is_server }) => ({
        url: `workflow_group_name=${workflow_group_name}&action=get_outbound_diff&account=${account}&template_name=${template_name}&html_only=true`
      }),
      transformResponse: (response) => {
        // Process the plain text response here
        return response; // Return the processed response
      },
      responseHandler: (response) => response.text(),
    }),
    inboundDiff: builder.query({
      query: ({ account, user, workflow_group_name, workflow_name, template_name, is_server }) => ({
        url: `workflow_group_name=${workflow_group_name}&action=get_inbound_diff&account=${account}&template_name=${template_name}&html_only=true`
      }),
      transformResponse: (response) => {
        // Process the plain text response here
        return response; // Return the processed response
      },
      responseHandler: (response) => response.text(),
    }),
    uploadTemplate : builder.mutation({
      query: ({account, file_name,workflow_group_name,type, file}) => ({
        url: `action=upload_template&account=${account}&file_name=${file_name}&workflow_group_name=${workflow_group_name}&type=${type}`,
        method: 'POST',
        body: file,
        formData: true,
      })
    }),
    getFolderList: builder.query({
      query: ({account, userspace, server}) => ({
        url: `action=list_templates&account=${account}&userspace=${userspace}&server=${server}&recursive=true&include_files=true`
      })
    }),
    exportDeployCSV: builder.query({
      query: ({account}) => ({
        url: `action=export_deploy_csv&account=${account}`,
        responseHandler: (response) => response.text()
      })
    }),
    getFileList: builder.query({
      query: ({account, wf_group_name, folder_path, userspace, server}) => ({
        url: `action=get_folder_files&account=${account}&wf_group_name=${wf_group_name}&folder_path=${folder_path}&userspace=${userspace}&server=${server}`
      })
    }),
    lockTemplateFolder: builder.mutation({
      query: ({account, data}) => ({
        url: `action=lock_folder&account=${account}`,
        method: "POST",
        body: data,
      })
    }),
    unLockTemplates: builder.mutation({
      query: ({account, data}) => ({
        url: `action=unlock_folder&account=${account}`,
        method: "POST",
        body: data,
      })
    }),
    deleteTemplatesFromDeployPage: builder.mutation({
      query: ({account, data}) => ({
        url: `action=delete_selected_items&account=${account}`,
        method: "POST",
        body: data,
      })
    }),
    deployTemplatesToServer: builder.mutation({
      query: ({account, data}) => ({
        url: `action=deploy_folder_to_server&account=${account}`,
        method: "POST",
        body: data,
      })
    }),
    getHL7DatatypeComponents: builder.query({
      query: ({account, data_type}) => ({
        url: `action=get_hl7_datatype_components&account=${account}&datatype=${data_type}`
      })
    }),
    downloadTradingPartnerCSV: builder.query({
      query: ({account}) => ({
        url: `action=download_trading_partner_csv&account=${account}`,
        responseHandler: (response) => response.text()
      })
    }),
    uploadTradingPartnerCSV: builder.mutation({
      query: ({account, file}) => ({
        url: `action=upload_trading_partner_csv&account=${account}`,
        method: 'POST',
        body: file
      })
    }),
    copyWorkflowGroup: builder.query({
      query: ({account, sourceWorkflowGroupName, workflowGroupName}) => {
        return `action=copy_workflow_group&account=${account}&workflow_group_name=${sourceWorkflowGroupName}&new_workflow_group_name=${workflowGroupName}`
      }
    }),
    sendAIMessage: builder.query({
      query: ({account, message}) => {
        return `action=send_ai_chat_message&account=${account}&message=${message}`
      }
    }),
    getAIHelpChatHistory: builder.query({
      query: ({account, limit, last_message_id}) => {
        return `action=get_ai_chat_history&account=${account}&last_message_id=${last_message_id}&limit=${limit}`
      }
    }),
    getAcceleratorList: builder.query({
      query: ({account, user}) => {
        return `action=get_accelerator_list&account=${account}&user=${user}`
      }
    }),
    installAccelerator: builder.mutation({
      query: ({account, user, package: packageUrl}) => {
        return {
          url: `action=install_accelerator&account=${account}&user=${user}&package=${encodeURIComponent(packageUrl)}`
        }
      }
    }),
    saveRemoteConnection: builder.mutation({
      query: ({ account, data }) => ({
        url: `action=save_remote_connection&account=${account}`,
        method: "POST",
        body: data,
      }),
    }),
    listRemoteConnections: builder.query({
      query: ({account}) => {
        return `action=list_remote_connections&account=${account}`
      }
    }),
    deleteRemoteConnection: builder.mutation({
      query: ({ account, data }) => {
        return {
          url: `action=delete_remote_connection&account=${account}`,
          method: 'POST',
          body: data
        }
      }
    }),
    testRemoteConnection: builder.query({
      query: ({ account, remote_url, connection_id }) => {
        let q = `action=test_remote_connection&account=${account}`;
        if (remote_url) q += `&remote_url=${encodeURIComponent(remote_url)}`;
        if (connection_id) q += `&connection_id=${encodeURIComponent(connection_id)}`;
        return q;
      }
    }),
    remoteServerLogin: builder.mutation({
      query: ({ account, data }) => ({
        url: `action=remote_server_login&account=${account}`,
        method: 'POST',
        body: data,
      }),
    }),
    connectRemoteServer: builder.mutation({
      query: ({ account, data }) => ({
        url: `action=connect&account=${account}`,
        method: 'POST',
        body: data,
      }),
    }),
    listRemoteTemplates: builder.query({
      query: ({ account, remote_token, remote_url, user, connection_id, include_files, recursive, wf_group_name }) => {
        let q = `action=list_remote_templates&account=${account}&remote_token=${encodeURIComponent(remote_token || '')}`;
        if (remote_url) q += `&remote_url=${encodeURIComponent(remote_url)}`;
        if (user) q += `&user=${encodeURIComponent(user)}`;
        if (connection_id) q += `&connection_id=${encodeURIComponent(connection_id)}`;
        if (include_files != null) q += `&include_files=${include_files}`;
        if (recursive != null) q += `&recursive=${recursive}`;
        if (wf_group_name) q += `&wf_group_name=${encodeURIComponent(wf_group_name)}`;
        return q;
      }
    }),
    getRemoteFolderFiles: builder.query({
      query: ({ account, remote_token, remote_url, user, connection_id, wf_group_name, folder_path }) => {
        let q = `action=get_remote_folder_files&account=${account}&remote_token=${encodeURIComponent(remote_token || '')}&wf_group_name=${encodeURIComponent(wf_group_name || '')}`;
        if (remote_url) q += `&remote_url=${encodeURIComponent(remote_url)}`;
        if (user) q += `&user=${encodeURIComponent(user)}`;
        if (connection_id) q += `&connection_id=${encodeURIComponent(connection_id)}`;
        if (folder_path) q += `&folder_path=${encodeURIComponent(folder_path)}`;
        return q;
      }
    }),
    deployToRemoteServer: builder.mutation({
      query: ({ account, data }) => ({
        url: `action=deploy_to_remote_server&account=${account}`,
        method: 'POST',
        body: data,
      }),
    }),
    getRemoteRevisionHistory: builder.query({
      query: ({ account, remote_token, remote_url, user, connection_id, wf_group_name, folder_path, template_name }) => {
        let q = `action=get_remote_revision_history&account=${account}&remote_token=${encodeURIComponent(remote_token || '')}&wf_group_name=${encodeURIComponent(wf_group_name || '')}&template_name=${encodeURIComponent(template_name || '')}`;
        if (remote_url) q += `&remote_url=${encodeURIComponent(remote_url)}`;
        if (user) q += `&user=${encodeURIComponent(user)}`;
        if (connection_id) q += `&connection_id=${encodeURIComponent(connection_id)}`;
        if (folder_path) q += `&folder_path=${encodeURIComponent(folder_path)}`;
        return q;
      }
    }),
    modifyRemoteConnections: builder.mutation({
      query: ({ account, data }) => ({
        url: `action=modify_remote_connections&account=${account}`,
        method: 'POST',
        body: data,
      }),
    }),

    // -------------------------------------------------------------------------
    // API / Service Manager (BR4.1 Step 1 + overview)
    // -------------------------------------------------------------------------
    getUserPermission: builder.query({
      query: ({ user }) => `action=get_user_permission&user=${encodeURIComponent(user)}`,
    }),
    apiServiceValidateServiceName: builder.query({
      query: ({ account, service_name, exclude_wfg_name, exclude_wf_name }) => {
        let url = `action=api_service_validate_service_name&account=${encodeURIComponent(account)}&service_name=${encodeURIComponent(service_name)}`;
        if (exclude_wfg_name) url += `&exclude_wfg_name=${encodeURIComponent(exclude_wfg_name)}`;
        if (exclude_wf_name) url += `&exclude_wf_name=${encodeURIComponent(exclude_wf_name)}`;
        return url;
      },
    }),
    apiServiceValidateWorkflowName: builder.query({
      query: ({ account, wfg_name, wf_name }) =>
        `action=api_service_validate_workflow_name&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}`,
    }),
    apiServiceValidateCustomUrl: builder.query({
      query: ({ account, custom_url, custom_url_query, wfg_name, wf_name }) => {
        let url = `action=api_service_validate_custom_url&account=${encodeURIComponent(account)}&custom_url=${encodeURIComponent(custom_url ?? "")}`;
        if (custom_url_query) url += `&custom_url_query=${encodeURIComponent(custom_url_query)}`;
        if (wfg_name) url += `&wfg_name=${encodeURIComponent(wfg_name)}`;
        if (wf_name) url += `&wf_name=${encodeURIComponent(wf_name)}`;
        return url;
      },
    }),
    serviceWorkflowList: builder.query({
      query: ({ account }) => `action=service_workflow_list&account=${encodeURIComponent(account)}`,
    }),
    serviceAdapterList: builder.query({
      query: ({ account, is_adapter = true }) =>
        `action=service_adapter_list&account=${encodeURIComponent(account)}&is_adapter=${is_adapter}`,
    }),
    getApiServiceList: builder.query({
      query: (params = {}) => {
        const parts = ["action=api_service_list"];
        const add = (key, value) => {
          if (value != null && value !== "") {
            parts.push(`${key}=${encodeURIComponent(String(value))}`);
          }
        };
        add("account", params.account);
        add("accounts", params.accounts);
        add("search", params.search);
        add("adapter_type", params.adapter_type);
        add("enabled", params.enabled);
        add("filter_account", params.filter_account);
        add("wfg_name", params.wfg_name);
        add("wf_name", params.wf_name);
        add("wfg_names", params.wfg_names);
        add("wf_names", params.wf_names);
        if (params.offset != null) add("offset", params.offset);
        if (params.limit != null) add("limit", params.limit);
        return parts.join("&");
      },
      providesTags: [{ type: "Services", id: "LIST" }],
    }),
    changeServiceStatus: builder.mutation({
      query: ({ account, wfg_name, wf_name, service_status, request_ip = "react-workbench", source }) => {
        let url = `action=change_status&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}&service_status=${encodeURIComponent(service_status)}&request_ip=${encodeURIComponent(request_ip)}`;
        if (source) url += `&source=${encodeURIComponent(source)}`;
        return { url, method: "GET" };
      },
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),
    deleteApiService: builder.mutation({
      query: ({ account, wfg_name, wf_name, source }) => {
        let url = `action=api_service_delete&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}`;
        if (source) url += `&source=${encodeURIComponent(source)}`;
        return { url, method: "GET" };
      },
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),
    getServiceExecHistory: builder.query({
      query: ({ account, wfg_name, wf_name, rowcnt = 50 }) =>
        `action=get_service_exec_history&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}&rowcnt=${rowcnt}`,
    }),
    getApiServiceDetail: builder.query({
      query: ({ account, wfg_name, wf_name }) =>
        `action=api_service_get_detail&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}`,
    }),
    apiServiceUpdate: builder.mutation({
      query: ({
        account,
        wfg_name,
        wf_name,
        service_name,
        service_description,
        workflow_comments,
        conformance_execute_within_ms,
        conformance_errors_only,
        custom_url,
        custom_url_query,
        api_version,
        source,
      }) => {
        let url = `action=api_service_update&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}`;
        if (service_name != null) url += `&service_name=${encodeURIComponent(service_name)}`;
        if (service_description != null) url += `&service_description=${encodeURIComponent(service_description)}`;
        if (workflow_comments != null) url += `&workflow_comments=${encodeURIComponent(workflow_comments)}`;
        if (conformance_execute_within_ms != null) {
          url += `&conformance_execute_within_ms=${encodeURIComponent(String(conformance_execute_within_ms))}`;
        }
        if (conformance_errors_only != null) {
          url += `&conformance_errors_only=${conformance_errors_only ? "true" : "false"}`;
        }
        if (custom_url != null) url += `&custom_url=${encodeURIComponent(custom_url)}`;
        if (custom_url_query != null) url += `&custom_url_query=${encodeURIComponent(custom_url_query)}`;
        if (api_version != null) url += `&api_version=${encodeURIComponent(api_version)}`;
        if (source) url += `&source=${encodeURIComponent(source)}`;
        return { url, method: "POST" };
      },
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),
    apiServiceChangeStatus: builder.mutation({
      query: ({ account, wfg_name, wf_name, service_status, request_ip, source }) => {
        let url = `action=change_status&account=${encodeURIComponent(account)}&wfg_name=${encodeURIComponent(wfg_name)}&wf_name=${encodeURIComponent(wf_name)}&service_status=${service_status === true || service_status === "true" ? "true" : "false"}`;
        if (request_ip) url += `&request_ip=${encodeURIComponent(request_ip)}`;
        if (source) url += `&source=${encodeURIComponent(source)}`;
        return { url, method: "POST" };
      },
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),
    getApiServiceMetricsBySource: builder.query({
      query: (params = {}) => {
        const parts = ["action=api_service_metrics_by_source"];
        const add = (key, value) => {
          if (value != null && value !== "") parts.push(`${key}=${encodeURIComponent(String(value))}`);
        };
        add("account", params.account);
        add("accounts", params.accounts);
        add("wfg_name", params.wfg_name);
        add("wf_name", params.wf_name);
        add("wfg_names", params.wfg_names);
        add("wf_names", params.wf_names);
        add("adapter_types", params.adapter_types);
        add("start_date", params.start_date);
        add("end_date", params.end_date);
        if (params.rowcnt != null) add("rowcnt", params.rowcnt);
        return parts.join("&");
      },
    }),
    getApiServiceMetricsConformanceOutliers: builder.query({
      query: (params = {}) => {
        const parts = ["action=api_service_metrics_conformance_outliers"];
        const add = (key, value) => {
          if (value != null && value !== "") parts.push(`${key}=${encodeURIComponent(String(value))}`);
        };
        add("account", params.account);
        add("accounts", params.accounts);
        add("wfg_name", params.wfg_name);
        add("wf_name", params.wf_name);
        add("wfg_names", params.wfg_names);
        add("wf_names", params.wf_names);
        add("adapter_types", params.adapter_types);
        add("start_date", params.start_date);
        add("end_date", params.end_date);
        if (params.rowcnt != null) add("rowcnt", params.rowcnt);
        if (params.limit != null) add("limit", params.limit);
        return parts.join("&");
      },
    }),
    getApiServiceMetricsRolling24h: builder.query({
      query: (params = {}) => {
        const parts = ["action=api_service_metrics_rolling_24h"];
        const add = (key, value) => {
          if (value != null && value !== "") parts.push(`${key}=${encodeURIComponent(String(value))}`);
        };
        add("account", params.account);
        add("accounts", params.accounts);
        add("wfg_name", params.wfg_name);
        add("wf_name", params.wf_name);
        add("wfg_names", params.wfg_names);
        add("wf_names", params.wf_names);
        add("start_date", params.start_date);
        add("end_date", params.end_date);
        if (params.include_by_source != null) add("include_by_source", params.include_by_source);
        if (params.rowcnt != null) add("rowcnt", params.rowcnt);
        return parts.join("&");
      },
    }),
    getApiServiceMetricsSingular: builder.query({
      query: (params = {}) => {
        const parts = ["action=api_service_metrics_singular"];
        const add = (key, value) => {
          if (value != null && value !== "") parts.push(`${key}=${encodeURIComponent(String(value))}`);
        };
        add("account", params.account);
        add("wfg_name", params.wfg_name);
        add("wf_name", params.wf_name);
        add("start_date", params.start_date);
        add("end_date", params.end_date);
        if (params.source_rowcnt != null) add("source_rowcnt", params.source_rowcnt);
        if (params.outlier_limit != null) add("outlier_limit", params.outlier_limit);
        return parts.join("&");
      },
    }),

    // -------------------------------------------------------------------------
    // API Management
    // -------------------------------------------------------------------------
    getApiTokens: builder.query({
      query: ({ account }) => `action=get_api_tokens&account=${account}`,
    }),
    disableApiToken: builder.mutation({
      query: ({ tokenid, tokenname }) => ({
        url: `action=disable_api_token&tokenid=${tokenid}&tokenname=${encodeURIComponent(tokenname)}`,
        method: 'GET',
      }),
    }),
    enableApiToken: builder.mutation({
      query: ({ tokenid, tokenname }) => ({
        url: `action=enable_api_token&tokenid=${tokenid}&tokenname=${encodeURIComponent(tokenname)}`,
        method: 'GET',
      }),
    }),
    deleteApiToken: builder.mutation({
      query: ({ tokenid, tokenname }) => ({
        url: `action=delete_api_token&tokenid=${tokenid}&tokenname=${encodeURIComponent(tokenname)}`,
        method: 'GET',
      }),
    }),
    getApiTokenHistory: builder.query({
      query: ({ tokenid }) => `action=get_api_token_history&tokenid=${tokenid}`,
    }),
    getEligibleWorkflows: builder.query({
      query: ({ account }) => `action=get_eligible_workflows&account=${account}`,
    }),
    createApiToken: builder.mutation({
      query: ({ tokenname, account, assignedWorkflows = [], tokenType, apiAccess, executeWorkflowScope }) => ({
        url: 'action=create_api_token',
        method: 'POST',
        body: new URLSearchParams({
          tokenname,
          account,
          token_type: tokenType || 'STATIC',
          api_access: apiAccess || 'READ_WRITE',
          execute_workflow_scope: executeWorkflowScope || 'ALL',
          assignedworkflows: JSON.stringify(
            assignedWorkflows.map((wf) => ({ workflowGroup: wf.workflowGroup, workflowName: wf.workflowName }))
          ),
        }),
      }),
    }),
    updateApiToken: builder.mutation({
      query: ({ tokenid, assignedWorkflows = [], apiAccess, executeWorkflowScope }) => ({
        url: 'action=update_api_token',
        method: 'POST',
        body: new URLSearchParams({
          tokenid: String(tokenid),
          api_access: apiAccess || '',
          execute_workflow_scope: executeWorkflowScope || '',
          assignedworkflows: JSON.stringify(
            assignedWorkflows.map((wf) => ({ workflowGroup: wf.workflowGroup, workflowName: wf.workflowName }))
          ),
        }),
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetHelpTreeListQuery,
  useGetInboundTemplateListQuery,
  useGetCopyInboundTemplateListQuery,
  useGetCopyOutboundTemplateListQuery,
  useGetInboundTemplateQuery,
  useGetOutboundTemplateListQuery,
  useGetOutboundTemplateQuery,
  useSaveUserInboundTemplateQuery,
  useSaveInboundTemplateQuery,
  useGetEDIVersionsQuery,
  useGetTransSetsQuery,
  useGetTriggerEventsQuery,
  useGetPdcListQuery,
  useUpdateWorkflowNameQuery,
  useAddWorkflowGroupMutation,
  useGetNCPDPVersionsQuery,
  useMoveInboundTemplateMutation,
  useGetDataDepotListQuery,
  useGetDataDepotFieldListQuery,
  useGetUnsavedTemplatesQuery,
  useGetUnsavedInboundTemplatesQuery,
  useValidateDatePatternQuery,
  useValidateInboundTemplateQuery,
  useGetDatatypePropertiesQuery,
  useGetSegmentIdsQuery,
  useGetRepositoryTemplatesQuery,
  useGetRepositoryFilesQuery,
  useAddNewInboundTemplateQuery,
  useGetSchemaListQuery,
  useSendLastSelectedTabQuery,
  useGetTrackingReportsQuery,
  useLazyGetTrackingReportsQuery,
  useTrackingReportAutomappingQuery,
  useLazyTrackingReportAutomappingQuery,
  useUploadTemplateMutation,
  useGetFolderListQuery,
  useGetAcceleratorListQuery,
  useInstallAcceleratorMutation,
  useLazyExportDeployCSVQuery,
  useModifyRemoteConnectionsMutation,
  useGetWorkflowsCopyQuery,
  useGetAccountsQuery,
  useGetServerCommonNameListQuery,
  useGetSchedulerHomeDataQuery,
  useGetSchedulerReferenceDataQuery,
  useGetScheduleExecHistoryQuery,
  useLazyGetScheduleDetailsQuery,
  useSaveScheduleDetailsMutation,
  useSaveTemplatesMutation,
  useChangeScheduleStatusMutation,
  useDeleteScheduleMutation,
  useGetApiTokensQuery,
  useDisableApiTokenMutation,
  useEnableApiTokenMutation,
  useDeleteApiTokenMutation,
  useGetApiTokenHistoryQuery,
  useGetEligibleWorkflowsQuery,
  useCreateApiTokenMutation,
  useUpdateApiTokenMutation,
  useGetUserPermissionQuery,
  useLazyGetUserPermissionQuery,
  useApiServiceValidateServiceNameQuery,
  useLazyApiServiceValidateServiceNameQuery,
  useApiServiceValidateWorkflowNameQuery,
  useLazyApiServiceValidateWorkflowNameQuery,
  useApiServiceValidateCustomUrlQuery,
  useLazyApiServiceValidateCustomUrlQuery,
  useServiceWorkflowListQuery,
  useServiceAdapterListQuery,
  useGetApiServiceListQuery,
  useLazyGetApiServiceListQuery,
  useChangeServiceStatusMutation,
  useDeleteApiServiceMutation,
  useGetServiceExecHistoryQuery,
  useGetApiServiceDetailQuery,
  useApiServiceUpdateMutation,
  useApiServiceChangeStatusMutation,
  useGetApiServiceMetricsBySourceQuery,
  useLazyGetApiServiceMetricsBySourceQuery,
  useGetApiServiceMetricsConformanceOutliersQuery,
  useLazyGetApiServiceMetricsConformanceOutliersQuery,
  useGetApiServiceMetricsRolling24hQuery,
  useLazyGetApiServiceMetricsRolling24hQuery,
  useGetApiServiceMetricsSingularQuery,
  useLazyGetApiServiceMetricsSingularQuery,
} = api;
